import { useMemo } from "react";
import { useAuth } from "../context/AuthContext";
import {
  getFeatureFlagsFromUser,
  isFeatureFlagEnabled,
  type FeatureFlagKey,
  type FeatureFlagsRecord,
} from "../utils/featureFlags";

export type UseFeatureFlagsResult = {
  flags: FeatureFlagsRecord | undefined;
  isEnabled: (key: FeatureFlagKey) => boolean;
  isAdCenterEnabled: boolean;
  isAiEnabled: boolean;
  isUserManagementEnabled: boolean;
  isCircleManagementEnabled: boolean;
  isTeamManagementEnabled: boolean;
  isFamilyManagementEnabled: boolean;
  /** TipTap bold/italic/lists in notice body — opt-in, off by default. */
  isNoticeBodyFormattingEnabled: boolean;
  /** Image Studio card + route in Create — opt-in, off by default. */
  isImageStudioEnabled: boolean;
  /** PLANNER on the Calendar and the Planners pages — opt-in, off by default. */
  isPlannerEnabled: boolean;
};

export function useFeatureFlags(): UseFeatureFlagsResult {
  const { user } = useAuth();

  return useMemo(() => {
    const flags = getFeatureFlagsFromUser(user);
    const isEnabled = (key: FeatureFlagKey) => isFeatureFlagEnabled(flags, key);

    return {
      flags,
      isEnabled,
      isAdCenterEnabled: isEnabled("adCenter"),
      isAiEnabled: isEnabled("ai"),
      isUserManagementEnabled: isEnabled("userManagement"),
      isCircleManagementEnabled: isEnabled("circleManagement"),
      isTeamManagementEnabled: isEnabled("teamManagement"),
      isFamilyManagementEnabled: isEnabled("familyManagement"),
      isNoticeBodyFormattingEnabled: isEnabled("noticeBodyFormatting"),
      isImageStudioEnabled: isEnabled("imageStudio"),
      isPlannerEnabled: isEnabled("planner"),
    };
  }, [user]);
}
